"use client"

import { useState, useEffect } from "react"
import {
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material"
import { Add, Delete, ExpandMore } from "@mui/icons-material"
import adminSettingsService from "../../services/AdminSettingsService"
import { toast } from "react-toastify";

export default function AdminSettingConfig() {
  const [loading, setLoading] = useState(true);
  const [cancellationEditId, setCancellationEditId] = useState(null);
  const [subscriptionEditId, setSubscriptionEditId] = useState(null);
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
  const [config, setConfig] = useState({
    title: "Cancellation Policy",
    settings: {
      cancellation_reasons: {
        customer: [],
        driver: [],
      },
    },
  })

  // Fixed functions for handling driver reasons
  const addDriverReason = () => {
    setConfig(prevConfig => ({
      ...prevConfig,
      settings: {
        ...prevConfig.settings,
        cancellation_reasons: {
          ...prevConfig.settings?.cancellation_reasons,
          driver: [...(prevConfig.settings?.cancellation_reasons?.driver || []), { display_text: "" }],
        },
      },
    }))
  }

  const updateDriverReason = (index, value) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      settings: {
        ...prevConfig.settings,
        cancellation_reasons: {
          ...prevConfig.settings?.cancellation_reasons,
          driver: (prevConfig.settings?.cancellation_reasons?.driver || []).map((reason, i) =>
            i === index ? { ...reason, display_text: value } : reason
          ),
        },
      },
    }))
  }

  const removeDriverReason = (index) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      settings: {
        ...prevConfig.settings,
        cancellation_reasons: {
          ...prevConfig.settings?.cancellation_reasons,
          driver: (prevConfig.settings?.cancellation_reasons?.driver || []).filter((_, i) => i !== index),
        },
      },
    }))
  }

  // Fixed functions for handling customer reasons
  const addCustomerReason = () => {
    setConfig(prevConfig => ({
      ...prevConfig,
      settings: {
        ...prevConfig.settings,
        cancellation_reasons: {
          ...prevConfig.settings?.cancellation_reasons,
          customer: [...(prevConfig.settings?.cancellation_reasons?.customer || []), { display_text: "" }],
        },
      },
    }))
  }

  const updateCustomerReason = (index, value) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      settings: {
        ...prevConfig.settings,
        cancellation_reasons: {
          ...prevConfig.settings?.cancellation_reasons,
          customer: (prevConfig.settings?.cancellation_reasons?.customer || []).map((reason, i) =>
            i === index ? { ...reason, display_text: value } : reason
          ),
        },
      },
    }))
  }

  const removeCustomerReason = (index) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      settings: {
        ...prevConfig.settings,
        cancellation_reasons: {
          ...prevConfig.settings?.cancellation_reasons,
          customer: (prevConfig.settings?.cancellation_reasons?.customer || []).filter((_, i) => i !== index),
        },
      },
    }))
  }

  const updateTitle = (value) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      title: value,
    }))
  }

  const saveConfiguration = async () => {
    try {
      const payload = {
        categoryKey: "cancellation_policy",
        categoryName: config?.title,
        settings: config?.settings,
      };

      let response;
      if (cancellationEditId) {
        response = await adminSettingsService.updateSetting(cancellationEditId, payload);
      } else {
        response = await adminSettingsService.createSetting(payload);
      }

      console.log("Saved successfully:", response);
      toast.success("Configuration saved successfully!");
    } catch (error) {
      console.error("Error saving configuration:", error);
      toast.error(error.message || "Error saving configuration. Please try again.");
    }
  };

  // Add this function to refresh config from API
  const handleReset = async () => {
    setLoading(true);
    try {
      const data = await adminSettingsService.getAllSettings();
      if (data?.data?.[0]) {
        const settingsData = data.data[0];
        setConfig({
          title: settingsData.title || "Cancellation Policy",
          settings: {
            cancellation_reasons: {
              customer: settingsData.settings?.cancellation_reasons?.customer || [],
              driver: settingsData.settings?.cancellation_reasons?.driver || [],
            },
          },
        });
        setCancellationEditId(settingsData.id);
      }
    } catch (error) {
      toast.error(error.message || "Error resetting configuration.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionToggle = async (event) => {
    const enabled = event.target.checked;
    setSubscriptionEnabled(enabled);
    try {
      const payload = {
        categoryKey: "subscription_feature",
        categoryName: "Subscription Feature",
        settings: { enabled },
      };
      if (subscriptionEditId) {
        await adminSettingsService.updateSetting(subscriptionEditId, payload);
      } else {
        const res = await adminSettingsService.createSetting(payload);
        setSubscriptionEditId(res?.data?.id);
      }
      toast.success(`Subscription feature ${enabled ? "enabled" : "disabled"} successfully!`);
    } catch (error) {
      setSubscriptionEnabled(!enabled);
      toast.error("Failed to update subscription setting.");
    }
  };

  useEffect(() => {
    const fetchAllSettings = async () => {
      try {
        const data = await adminSettingsService.getAllSettings();
        console.log("Fetched all settings:", data);

        const allSettings = data?.data || [];
        
        // Load cancellation policy
        const cancellationSetting = allSettings.find(s => s.categoryKey === "cancellation_policy");
        if (cancellationSetting) {
          setConfig({
            title: cancellationSetting.title || "Cancellation Policy",
            settings: {
              cancellation_reasons: {
                customer: cancellationSetting.settings?.cancellation_reasons?.customer || [],
                driver: cancellationSetting.settings?.cancellation_reasons?.driver || [],
              },
            },
          });
          setCancellationEditId(cancellationSetting.id);
        }

        // Load subscription feature setting
        const subscriptionSetting = allSettings.find(s => s.categoryKey === "subscription_feature");
        if (subscriptionSetting) {
          setSubscriptionEnabled(subscriptionSetting.settings?.enabled || false);
          setSubscriptionEditId(subscriptionSetting.id);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllSettings();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <CardContent>
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
          <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" component="h3">
                Subscription Feature
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enable or disable the subscription feature for customers
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={subscriptionEnabled}
                  onChange={handleSubscriptionToggle}
                  color="primary"
                />
              }
              label={subscriptionEnabled ? "Enabled" : "Disabled"}
            />
          </CardContent>
        </Card>
      </CardContent>

      <CardContent>
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <Typography variant="h6" component="h3" sx={{m: '8px'}}>
            Cancellation Policy
          </Typography>
          
          <CardContent>
            <Accordion defaultExpanded style={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Driver Reasons
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Button variant="outlined" startIcon={<Add />} onClick={addDriverReason} size="small">
                    Add Driver Reason
                  </Button>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {(config?.settings?.cancellation_reasons?.driver || []).map((reason, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TextField
                        label={`Reason ${index + 1}`}
                        value={reason?.display_text || ""}
                        onChange={(e) => updateDriverReason(index, e.target.value)}
                        placeholder="Enter driver cancellation reason"
                        fullWidth
                        variant="outlined"
                        size="small"
                      />
                      <IconButton onClick={() => removeDriverReason(index)} color="error" size="small">
                        <Delete />
                      </IconButton>
                    </Box>
                  ))}
                  {(config?.settings?.cancellation_reasons?.driver || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                      No driver reasons added yet. Click "Add Driver Reason" to get started.
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded style={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Customer Reasons
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Button variant="outlined" startIcon={<Add />} onClick={addCustomerReason} size="small">
                    Add Customer Reason
                  </Button>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {(config?.settings?.cancellation_reasons?.customer || []).map((reason, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <TextField
                        label={`Reason ${index + 1}`}
                        value={reason?.display_text || ""}
                        onChange={(e) => updateCustomerReason(index, e.target.value)}
                        placeholder="Enter customer cancellation reason"
                        fullWidth
                        variant="outlined"
                        size="small"
                      />
                      <IconButton onClick={() => removeCustomerReason(index)} color="error" size="small">
                        <Delete />
                      </IconButton>
                    </Box>
                  ))}
                  {(config?.settings?.cancellation_reasons?.customer || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                      No customer reasons added yet. Click "Add Customer Reason" to get started.
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </CardContent>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, p: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleReset}
              sx={{ backgroundColor: "#f87171"
                , "&:hover": { backgroundColor: "#f87171" }
              }}
            >
              Reset
            </Button>
            <Button variant="contained" size="large" onClick={saveConfiguration}>
              {!cancellationEditId ? "Save Configuration" : "Update Configuration"}
            </Button>
          </Box>
        </Card>
      </CardContent>
    </Box>
  )
}
